/**
 * AuthZEN Subject for PDP Evaluation Requests
 *
 * The BFF proxy owns subject resolution — it overrides the subject in
 * every PDP evaluation request with the canonical account ARN from the
 * server-side session (sourced from the IdP JWT / Membership service).
 *
 * The frontend never constructs, resolves, or selects ARNs.
 * It sends a structurally valid placeholder that the BFF replaces.
 *
 * @see IdP/docs/federation/standards_based_identity_mapping.md
 */

/** Subject structure for AuthZEN evaluation requests */
export interface AuthzenSubject {
  readonly type: 'account';
  readonly id: string;
}

/**
 * Return a placeholder AuthZEN subject for PDP evaluation requests.
 *
 * The BFF proxy owns subject resolution — it overrides whatever
 * subject the frontend sends with the canonical account ARN from the
 * server-side session (sourced from the IdP JWT / Membership service).
 * The frontend never constructs or selects ARNs.
 *
 * We still need to send *something* structurally valid so the request
 * body passes basic JSON schema checks in transit.
 *
 * @returns Promise resolving to a placeholder subject (overridden server-side)
 */
export async function getAuthzenSubject(): Promise<AuthzenSubject> {
  return { type: 'account', id: 'session' };
}

/**
 * No-op — subject cache is no longer needed since the BFF owns resolution.
 * Kept for API compatibility with callers that may invoke this on logout.
 */
export function invalidateSubjectCache(): void {
  // no-op
}
